import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, FileText, Calendar, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Modal, { ModalCloseButton } from './ui/Modal';

const NotesSection = ({ pmrn, API_BASE_URL }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({ Additional_Notes: '', id: null });
  const [clinicians, setClinicians] = useState({});
  const [nonClinicians, setNonClinicians] = useState(new Set());
  const hasCheckedCurrentUser = useRef(false);

  // Check if current user is a clinician (runs only once)
  useEffect(() => {
    const checkCurrentUser = async () => {
      if (!user?.user_id || hasCheckedCurrentUser.current) return;
      
      hasCheckedCurrentUser.current = true;

      try {
        const res = await fetch(`${API_BASE_URL}/clinicians/user/${user.user_id}`);
        if (res.ok) {
          const data = await res.json();
          const fullName = data.firstName && data.lastName
            ? `Dr. ${data.firstName} ${data.lastName}`
            : 'You';
          setClinicians((prev) => ({ ...prev, [user.user_id]: fullName }));
        } else {
          // Current user is not a clinician - this is okay
          setNonClinicians((prev) => new Set([...prev, user.user_id]));
          setClinicians((prev) => ({ ...prev, [user.user_id]: user.username || 'You' }));
        }
      } catch (err) {
        // Silent failure - user is not a clinician
        setNonClinicians((prev) => new Set([...prev, user.user_id]));
        setClinicians((prev) => ({ ...prev, [user.user_id]: user.username || 'You' }));
      }
    };

    checkCurrentUser();
  }, [user?.user_id, user?.username, API_BASE_URL]);

  // Fetch clinician name by userID
  const fetchClinicianName = useCallback(
    async (userId) => {
      // Skip if it's the current user (already handled)
      if (userId === user?.user_id) {
        return clinicians[userId] || 'You';
      }

      // Return cached value if available
      if (clinicians[userId]) return clinicians[userId];
      
      // Return fallback if already identified as non-clinician
      if (nonClinicians.has(userId)) {
        return clinicians[userId] || `User #${userId}`;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/clinicians/user/${userId}`);
        
        if (res.ok) {
          const data = await res.json();
          const fullName = data.firstName && data.lastName
            ? `Dr. ${data.firstName} ${data.lastName}`
            : data.firstName || data.lastName || `User #${userId}`;

          setClinicians((prev) => ({ ...prev, [userId]: fullName }));
          return fullName;
        } else {
          // Not a clinician
          const fallback = `User #${userId}`;
          setNonClinicians((prev) => new Set([...prev, userId]));
          setClinicians((prev) => ({ ...prev, [userId]: fallback }));
          return fallback;
        }
      } catch (err) {
        const fallback = `User #${userId}`;
        setNonClinicians((prev) => new Set([...prev, userId]));
        setClinicians((prev) => ({ ...prev, [userId]: fallback }));
        return fallback;
      }
    },
    [API_BASE_URL, clinicians, nonClinicians, user?.user_id]
  );

  const fetchNotes = useCallback(async () => {
    if (!pmrn) return;

    try {
      const res = await fetch(`${API_BASE_URL}/medical-history/patient/${pmrn}`);
      if (!res.ok) {
        console.error('Failed to fetch notes:', res.status);
        return;
      }

      const data = await res.json();

      // Filter only notes with Additional_Notes
      const notesOnly = data.filter((record) => record.Additional_Notes?.trim());
      notesOnly.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      setNotes(notesOnly);

      // Fetch clinician names for other users (not current user)
      const uniqueUserIds = [
        ...new Set(
          notesOnly
            .map((note) => note.updatedByClinicianId)
            .filter((id) => id != null && id !== user?.user_id)
        ),
      ];
      
      // Fetch names in parallel
      await Promise.allSettled(uniqueUserIds.map((id) => fetchClinicianName(id)));
    } catch (err) {
      console.error('Failed to fetch notes:', err);
    }
  }, [API_BASE_URL, pmrn, fetchClinicianName, user?.user_id]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAdd = () => {
    setFormData({ Additional_Notes: '', id: null });
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleEdit = (note) => {
    setFormData({ Additional_Notes: note.Additional_Notes, id: note.id });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (noteId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/medical-history/${noteId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setDeleteTarget(null);
      fetchNotes();
    } catch (err) {
      console.error(err);
      alert('Failed to delete note.');
    }
  };

  const handleSubmit = async () => {
    if (!formData.Additional_Notes.trim()) {
      alert('Please enter a note.');
      return;
    }
    if (!user?.user_id) {
      alert('User not authenticated.');
      return;
    }

    const payload = {
      Additional_Notes: formData.Additional_Notes,
      PMRN: pmrn,
      updatedByClinicianId: user.user_id,
    };

    try {
      const res = await fetch(
        `${API_BASE_URL}/medical-history/${isEditing ? `/${formData.id}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) throw new Error('Request failed');

      setIsModalOpen(false);
      setFormData({ Additional_Notes: '', id: null });
      fetchNotes();
    } catch (err) {
      console.error(err);
      alert('Failed to save note.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getClinicianDisplay = (userId) => {
    if (userId === user?.user_id) {
      return 'You';
    }
    return clinicians[userId] || 'Loading...';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="text-teal-600" size={24} /> Notes
        </h3>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition shadow-md"
        >
          <Plus size={18} /> Add Note
        </button>
      </div>

      {/* Notes List */}
      <div className="space-y-4">
        {notes.length === 0 ? (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <FileText size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-500 text-lg">No notes yet</p>
          </div>
        ) : (
          notes.map((note) => {
            const isOwnNote = note.updatedByClinicianId === user?.user_id;
            return (
              <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition">
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <User size={16} className="text-teal-600" />
                      <span className="font-semibold text-gray-800">
                        {getClinicianDisplay(note.updatedByClinicianId)}
                      </span>
                      {isOwnNote && (
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium">
                          Your Note
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar size={14} />
                      <span>{formatDate(note.updatedAt)}</span>
                    </div>
                  </div>

                  {isOwnNote && (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleEdit(note)} 
                        className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition" 
                        title="Edit Note"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button 
                        onClick={() => setDeleteTarget(note)} 
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition" 
                        title="Delete Note"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-teal-500">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{note.Additional_Notes}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                {isEditing ? 'Edit Note' : 'Add New Note'}
              </h3>
              <ModalCloseButton onClose={() => setIsModalOpen(false)} />
            </div>
            <textarea
              value={formData.Additional_Notes}
              onChange={(e) => setFormData({ ...formData, Additional_Notes: e.target.value })}
              className="w-full border border-gray-300 rounded-lg p-3 min-h-[200px] focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Enter your note here..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition"
              >
                {isEditing ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
      >
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100">
            <div className="flex items-start justify-between gap-3 mb-4">
              <h3 className="text-xl font-bold text-gray-800">Confirm Delete</h3>
              <ModalCloseButton onClose={() => setDeleteTarget(null)} />
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this note?</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteTarget.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
      </Modal>
    </div>
  );
};

export default NotesSection;


// import React, { useState, useEffect, useCallback } from 'react';
// import { Plus, Edit2, Trash2, X, FileText, Calendar, User } from 'lucide-react';
// import { useAuth } from '../context/AuthContext';

// const NotesSection = ({
//   pmrn,
//   API_BASE_URL,
// }) => {
//   const { user } = useAuth();
//   const [notes, setNotes] = useState([]);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [deleteTarget, setDeleteTarget] = useState(null);
//   const [formData, setFormData] = useState({
//     Additional_Notes: '',
//     id: null,
//   });
//   const [clinicians, setClinicians] = useState({});
//   const [failedFetches, setFailedFetches] = useState(new Set());

//   // Fetch clinician by userID (not clinician ID)
//   const fetchClinicianName = useCallback(async (userId) => {
//     // Return cached value if exists
//     if (clinicians[userId]) {
//       return clinicians[userId];
//     }
    
//     // Don't retry failed fetches
//     if (failedFetches.has(userId)) {
//       return `Doctor (User #${userId})`;
//     }
    
//     try {
//       // Use /clinicians/user/{user_id} endpoint to fetch by userID
//       const res = await fetch(`${API_BASE_URL}/clinicians/user/${userId}`);
      
//       if (res.ok) {
//         const data = await res.json();
//         const fullName = data.firstName && data.lastName 
//           ? `Dr. ${data.firstName} ${data.lastName}` 
//           : `Doctor (User #${userId})`;
        
//         setClinicians(prev => ({ ...prev, [userId]: fullName }));
//         return fullName;
//       } else {
//         // Backend returned error - mark as failed
//         setFailedFetches(prev => new Set([...prev, userId]));
//         setClinicians(prev => ({ ...prev, [userId]: `Doctor (User #${userId})` }));
//         return `Doctor (User #${userId})`;
//       }
//     } catch (err) {
//       console.warn(`Could not fetch clinician for user ${userId}:`, err.message);
//       // Mark as failed to prevent repeated attempts
//       setFailedFetches(prev => new Set([...prev, userId]));
//       setClinicians(prev => ({ ...prev, [userId]: `Doctor (User #${userId})` }));
//       return `Doctor (User #${userId})`;
//     }
//   }, [API_BASE_URL, clinicians, failedFetches]);

//   // Fetch notes
//   const fetchNotes = useCallback(async () => {
//     if (!pmrn) return;
    
//     try {
//       const res = await fetch(`${API_BASE_URL}/medical-history/patient/${pmrn}`);
//       if (!res.ok) throw new Error('Failed to fetch notes');
      
//       const data = await res.json();
      
//       // Filter only records with Additional Notes
//       const notesOnly = data.filter(record => 
//         record.Additional_Notes && record.Additional_Notes.trim() !== ''
//       );
      
//       // Sort by date (newest first)
//       notesOnly.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      
//       setNotes(notesOnly);
      
//       // Fetch unique clinician names by userID
//       const uniqueUserIds = [...new Set(
//         notesOnly.map(note => note.updatedByClinicianId).filter(Boolean)
//       )];
      
//       // Fetch all clinician names using userID
//       uniqueUserIds.forEach(userId => {
//         fetchClinicianName(userId);
//       });
//     } catch (err) {
//       console.error('Failed to fetch notes:', err);
//     }
//   }, [API_BASE_URL, pmrn, fetchClinicianName]);

//   useEffect(() => {
//     fetchNotes();
//   }, [fetchNotes]);

//   const handleAdd = () => {
//     setFormData({ Additional_Notes: '', id: null });
//     setIsEditing(false);
//     setIsModalOpen(true);
//   };

//   const handleEdit = (note) => {
//     setFormData({ Additional_Notes: note.Additional_Notes, id: note.id });
//     setIsEditing(true);
//     setIsModalOpen(true);
//   };

//   const handleDelete = async (noteId) => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/medical-history/${noteId}`, {
//         method: 'DELETE',
//       });
//       if (!response.ok) throw new Error('Delete failed');
//       setDeleteTarget(null);
//       fetchNotes();
//     } catch (err) {
//       console.error('Error deleting note:', err);
//       alert('Failed to delete note. Please try again.');
//     }
//   };

//   const handleSubmit = async () => {
//     if (!formData.Additional_Notes.trim()) {
//       alert('Please enter a note before saving.');
//       return;
//     }

//     if (!user?.user_id) {
//       alert('Unable to save note. User not authenticated.');
//       return;
//     }

//     const payload = {
//       Additional_Notes: formData.Additional_Notes,
//       PMRN: pmrn,
//       updatedByClinicianId: user.user_id,
//     };

//     try {
//       const response = await fetch(
//         `${API_BASE_URL}/medical-history${isEditing ? `/${formData.id}` : ''}`,
//         {
//           method: isEditing ? 'PUT' : 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         }
//       );

//       if (!response.ok) throw new Error('Request failed');

//       setIsModalOpen(false);
//       setFormData({ Additional_Notes: '', id: null });
      
//       // Add current user to clinicians cache
//       if (!clinicians[user.user_id]) {
//         setClinicians(prev => ({ ...prev, [user.user_id]: 'You' }));
//       }
      
//       fetchNotes();
//     } catch (err) {
//       console.error('Error saving note:', err);
//       alert('Failed to save note. Please try again.');
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric',
//       hour: '2-digit',
//       minute: '2-digit'
//     });
//   };

//   const getClinicianDisplay = (userId) => {
//     if (userId === user?.user_id) {
//       return 'You';
//     }
//     return clinicians[userId] || 'Loading...';
//   };

//   return (
//     <div className="space-y-4">
//       {/* Header */}
//       <div className="flex justify-between items-center">
//         <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
//           <FileText className="text-teal-600" size={24} />
//           Notes
//         </h3>
//         <button
//           onClick={handleAdd}
//           className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition shadow-md"
//         >
//           <Plus size={18} />
//           Add Note
//         </button>
//       </div>

//       {/* Notes List */}
//       <div className="space-y-4">
//         {notes.length === 0 ? (
//           <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
//             <FileText size={48} className="mx-auto text-gray-400 mb-3" />
//             <p className="text-gray-500 text-lg">No notes yet</p>
//             <p className="text-gray-400 text-sm mt-1">Click "Add Note" to create your first note</p>
//           </div>
//         ) : (
//           notes.map((note) => {
//             const isOwnNote = note.updatedByClinicianId === user?.user_id;
//             return (
//               <div
//                 key={note.id}
//                 className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition"
//               >
//                 {/* Note Header */}
//                 <div className="flex justify-between items-start mb-3">
//                   <div className="flex-1">
//                     <div className="flex items-center gap-2 mb-1">
//                       <User size={16} className="text-teal-600" />
//                       <span className="font-semibold text-gray-800">
//                         {getClinicianDisplay(note.updatedByClinicianId)}
//                       </span>
//                       {isOwnNote && (
//                         <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium">
//                           Your Note
//                         </span>
//                       )}
//                     </div>
//                     <div className="flex items-center gap-2 text-sm text-gray-500">
//                       <Calendar size={14} />
//                       <span>{formatDate(note.updatedAt)}</span>
//                     </div>
//                   </div>
                  
//                   {/* Action Buttons - Only for own notes */}
//                   {isOwnNote && (
//                     <div className="flex gap-2">
//                       <button
//                         onClick={() => handleEdit(note)}
//                         className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition"
//                         title="Edit Note"
//                       >
//                         <Edit2 size={18} />
//                       </button>
//                       <button
//                         onClick={() => setDeleteTarget(note)}
//                         className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
//                         title="Delete Note"
//                       >
//                         <Trash2 size={18} />
//                       </button>
//                     </div>
//                   )}
//                 </div>

//                 {/* Note Content */}
//                 <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-teal-500">
//                   <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
//                     {note.Additional_Notes}
//                   </p>
//                 </div>
//               </div>
//             );
//           })
//         )}
//       </div>

//       {/* Add/Edit Modal */}
//       {isModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl">
//             <div className="flex justify-between items-center border-b px-6 py-4">
//               <h2 className="text-xl font-semibold text-teal-700 flex items-center gap-2">
//                 <FileText size={22} />
//                 {isEditing ? 'Edit Note' : 'Add Note'}
//               </h2>
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="text-gray-400 hover:text-gray-600 transition"
//               >
//                 <X size={24} />
//               </button>
//             </div>

//             <div className="p-6">
//               <div className="space-y-2">
//                 <label className="block text-sm font-medium text-gray-700">
//                   Note
//                 </label>
//                 <textarea
//                   placeholder="Enter detailed notes here..."
//                   className="w-full border border-gray-300 rounded-lg p-3 h-64 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
//                   value={formData.Additional_Notes}
//                   onChange={(e) => setFormData({ ...formData, Additional_Notes: e.target.value })}
//                   autoFocus
//                 />
//                 <p className="text-xs text-gray-500">
//                   {formData.Additional_Notes.length} characters
//                 </p>
//               </div>
//             </div>

//             <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg border-t">
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleSubmit}
//                 className="px-5 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition shadow-md"
//               >
//                 {isEditing ? 'Update Note' : 'Save Note'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       {deleteTarget && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
//             <div className="p-6 space-y-4">
//               <div className="flex items-center gap-3">
//                 <div className="p-3 bg-red-100 rounded-full">
//                   <Trash2 className="text-red-600" size={24} />
//                 </div>
//                 <h3 className="text-lg font-semibold text-gray-800">Delete Note</h3>
//               </div>
              
//               <p className="text-gray-600">
//                 Are you sure you want to delete this note? This action cannot be undone.
//               </p>

//               <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-red-500">
//                 <p className="text-sm text-gray-700 line-clamp-3">
//                   {deleteTarget.Additional_Notes}
//                 </p>
//               </div>
//             </div>

//             <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg border-t">
//               <button
//                 onClick={() => setDeleteTarget(null)}
//                 className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleDelete(deleteTarget.id)}
//                 className="px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition shadow-md"
//               >
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default NotesSection;

// import React, { useState, useEffect, useCallback } from 'react';
// import { Plus, Edit2, Trash2, X, FileText, Calendar, User } from 'lucide-react';
// import { useAuth } from '../context/AuthContext';

// const NotesSection = ({
//   pmrn,
//   API_BASE_URL,
// }) => {
//   const { user } = useAuth();
//   const [notes, setNotes] = useState([]);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [deleteTarget, setDeleteTarget] = useState(null);
//   const [formData, setFormData] = useState({
//     Additional_Notes: '',
//     id: null,
//   });
//   const [clinicians, setClinicians] = useState({});
//   const [failedFetches, setFailedFetches] = useState(new Set());

//   // Fetch clinician by userID (not clinician ID)
//   const fetchClinicianName = useCallback(async (userId) => {
//     // Return cached value if exists
//     if (clinicians[userId]) {
//       return clinicians[userId];
//     }
    
//     // Don't retry failed fetches
//     if (failedFetches.has(userId)) {
//       return `Doctor (User #${userId})`;
//     }
    
//     try {
//       // ✅ FIX: Use /clinicians/user/{user_id} endpoint to fetch by userID
//       const res = await fetch(`${API_BASE_URL}/clinicians/user/${userId}`);
      
//       if (res.ok) {
//         const data = await res.json();
//         const fullName = data.firstName && data.lastName 
//           ? `Dr. ${data.firstName} ${data.lastName}` 
//           : `Doctor (User #${userId})`;
        
//         setClinicians(prev => ({ ...prev, [userId]: fullName }));
//         return fullName;
//       } else {
//         // Backend returned error - mark as failed
//         setFailedFetches(prev => new Set([...prev, userId]));
//         setClinicians(prev => ({ ...prev, [userId]: `Doctor (User #${userId})` }));
//         return `Doctor (User #${userId})`;
//       }
//     } catch (err) {
//       console.warn(`Could not fetch clinician for user ${userId}:`, err.message);
//       // Mark as failed to prevent repeated attempts
//       setFailedFetches(prev => new Set([...prev, userId]));
//       setClinicians(prev => ({ ...prev, [userId]: `Doctor (User #${userId})` }));
//       return `Doctor (User #${userId})`;
//     }
//   }, [API_BASE_URL, clinicians, failedFetches]);

//   // Fetch notes
//   const fetchNotes = useCallback(async () => {
//     if (!pmrn) return;
    
//     try {
//       const res = await fetch(`${API_BASE_URL}/medical-history/patient/${pmrn}`);
//       if (!res.ok) throw new Error('Failed to fetch notes');
      
//       const data = await res.json();
      
//       // Filter only records with Additional Notes
//       const notesOnly = data.filter(record => 
//         record.Additional_Notes && record.Additional_Notes.trim() !== ''
//       );
      
//       // Sort by date (newest first)
//       notesOnly.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
      
//       setNotes(notesOnly);
      
//       // Fetch unique clinician names
//       const uniqueClinicianIds = [...new Set(
//         notesOnly.map(note => note.updatedByClinicianId).filter(Boolean)
//       )];
      
//       // Fetch all clinician names
//       uniqueClinicianIds.forEach(id => {
//         fetchClinicianName(id);
//       });
//     } catch (err) {
//       console.error('Failed to fetch notes:', err);
//     }
//   }, [API_BASE_URL, pmrn, fetchClinicianName]);

//   useEffect(() => {
//     fetchNotes();
//   }, [fetchNotes]);

//   const handleAdd = () => {
//     setFormData({ Additional_Notes: '', id: null });
//     setIsEditing(false);
//     setIsModalOpen(true);
//   };

//   const handleEdit = (note) => {
//     setFormData({ Additional_Notes: note.Additional_Notes, id: note.id });
//     setIsEditing(true);
//     setIsModalOpen(true);
//   };

//   const handleDelete = async (noteId) => {
//     try {
//       const response = await fetch(`${API_BASE_URL}/medical-history/${noteId}`, {
//         method: 'DELETE',
//       });
//       if (!response.ok) throw new Error('Delete failed');
//       setDeleteTarget(null);
//       fetchNotes();
//     } catch (err) {
//       console.error('Error deleting note:', err);
//       alert('Failed to delete note. Please try again.');
//     }
//   };

//   const handleSubmit = async () => {
//     if (!formData.Additional_Notes.trim()) {
//       alert('Please enter a note before saving.');
//       return;
//     }

//     if (!user?.user_id) {
//       alert('Unable to save note. User not authenticated.');
//       return;
//     }

//     const payload = {
//       Additional_Notes: formData.Additional_Notes,
//       PMRN: pmrn,
//       updatedByClinicianId: user.user_id,
//     };

//     try {
//       const response = await fetch(
//         `${API_BASE_URL}/medical-history${isEditing ? `/${formData.id}` : ''}`,
//         {
//           method: isEditing ? 'PUT' : 'POST',
//           headers: { 'Content-Type': 'application/json' },
//           body: JSON.stringify(payload),
//         }
//       );

//       if (!response.ok) throw new Error('Request failed');

//       setIsModalOpen(false);
//       setFormData({ Additional_Notes: '', id: null });
      
//       // Add current user to clinicians cache
//       if (!clinicians[user.user_id]) {
//         setClinicians(prev => ({ ...prev, [user.user_id]: 'You' }));
//       }
      
//       fetchNotes();
//     } catch (err) {
//       console.error('Error saving note:', err);
//       alert('Failed to save note. Please try again.');
//     }
//   };

//   const formatDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     const date = new Date(dateString);
//     return date.toLocaleDateString('en-US', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric',
//       hour: '2-digit',
//       minute: '2-digit'
//     });
//   };

//   const getClinicianDisplay = (clinicianId) => {
//     if (clinicianId === user?.user_id) {
//       return 'You';
//     }
//     return clinicians[clinicianId] || 'Loading...';
//   };

//   return (
//     <div className="space-y-4">
//       {/* Header */}
//       <div className="flex justify-between items-center">
//         <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
//           <FileText className="text-teal-600" size={24} />
//            Notes
//         </h3>
//         <button
//           onClick={handleAdd}
//           className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition shadow-md"
//         >
//           <Plus size={18} />
//           Add Note
//         </button>
//       </div>

//       {/* Notes List */}
//       <div className="space-y-4">
//         {notes.length === 0 ? (
//           <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
//             <FileText size={48} className="mx-auto text-gray-400 mb-3" />
//             <p className="text-gray-500 text-lg">No notes yet</p>
//             <p className="text-gray-400 text-sm mt-1">Click "Add Note" to create your first note</p>
//           </div>
//         ) : (
//           notes.map((note) => {
//             const isOwnNote = note.updatedByClinicianId === user?.user_id;
//             return (
//               <div
//                 key={note.id}
//                 className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition"
//               >
//                 {/* Note Header */}
//                 <div className="flex justify-between items-start mb-3">
//                   <div className="flex-1">
//                     <div className="flex items-center gap-2 mb-1">
//                       <User size={16} className="text-teal-600" />
//                       <span className="font-semibold text-gray-800">
//                         {getClinicianDisplay(note.updatedByClinicianId)}
//                       </span>
//                       {isOwnNote && (
//                         <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium">
//                           Your Note
//                         </span>
//                       )}
//                     </div>
//                     <div className="flex items-center gap-2 text-sm text-gray-500">
//                       <Calendar size={14} />
//                       <span>{formatDate(note.updatedAt)}</span>
//                     </div>
//                   </div>
                  
//                   {/* Action Buttons - Only for own notes */}
//                   {isOwnNote && (
//                     <div className="flex gap-2">
//                       <button
//                         onClick={() => handleEdit(note)}
//                         className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition"
//                         title="Edit Note"
//                       >
//                         <Edit2 size={18} />
//                       </button>
//                       <button
//                         onClick={() => setDeleteTarget(note)}
//                         className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
//                         title="Delete Note"
//                       >
//                         <Trash2 size={18} />
//                       </button>
//                     </div>
//                   )}
//                 </div>

//                 {/* Note Content */}
//                 <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-teal-500">
//                   <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
//                     {note.Additional_Notes}
//                   </p>
//                 </div>
//               </div>
//             );
//           })
//         )}
//       </div>

//       {/* Add/Edit Modal */}
//       {isModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg w-full max-w-2xl shadow-2xl">
//             <div className="flex justify-between items-center border-b px-6 py-4">
//               <h2 className="text-xl font-semibold text-teal-700 flex items-center gap-2">
//                 <FileText size={22} />
//                 {isEditing ? 'Edit Note' : 'Add Note'}
//               </h2>
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="text-gray-400 hover:text-gray-600 transition"
//               >
//                 <X size={24} />
//               </button>
//             </div>

//             <div className="p-6">
//               <div className="space-y-2">
//                 <label className="block text-sm font-medium text-gray-700">
//                   Note
//                 </label>
//                 <textarea
//                   placeholder="Enter detailed notes here..."
//                   className="w-full border border-gray-300 rounded-lg p-3 h-64 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
//                   value={formData.Additional_Notes}
//                   onChange={(e) => setFormData({ ...formData, Additional_Notes: e.target.value })}
//                   autoFocus
//                 />
//                 <p className="text-xs text-gray-500">
//                   {formData.Additional_Notes.length} characters
//                 </p>
//               </div>
//             </div>

//             <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg border-t">
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleSubmit}
//                 className="px-5 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition shadow-md"
//               >
//                 {isEditing ? 'Update Note' : 'Save Note'}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Modal */}
//       {deleteTarget && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
//             <div className="p-6 space-y-4">
//               <div className="flex items-center gap-3">
//                 <div className="p-3 bg-red-100 rounded-full">
//                   <Trash2 className="text-red-600" size={24} />
//                 </div>
//                 <h3 className="text-lg font-semibold text-gray-800">Delete Note</h3>
//               </div>
              
//               <p className="text-gray-600">
//                 Are you sure you want to delete this note? This action cannot be undone.
//               </p>

//               <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-red-500">
//                 <p className="text-sm text-gray-700 line-clamp-3">
//                   {deleteTarget.Additional_Notes}
//                 </p>
//               </div>
//             </div>

//             <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg border-t">
//               <button
//                 onClick={() => setDeleteTarget(null)}
//                 className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={() => handleDelete(deleteTarget.id)}
//                 className="px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition shadow-md"
//               >
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

// export default NotesSection;
